import axios, { AxiosResponse, AxiosRequestConfig } from 'axios';
import { isEmptyString, getRandomCode } from '@/nmb/util';
import app from '@/nmb/app';
import { Commit } from 'vuex';
import { authApiBefore } from '@/nmb/middleware/authApiMiddleware';

type Method = 'GET' | 'POST' | 'DELETE';

interface ITypes {
  REQUEST: string;
  SUCCESS: string;
  FAILED: string;
}

const createType = (perfix: string): ITypes => {
  return {
    REQUEST: perfix + '_REQUEST',
    SUCCESS: perfix + '_SUCCESS',
    FAILED: perfix + '_FAILED'
  };
};

const getApiFetchOptions = (
  apiPath: string,
  method: string = 'GET',
  opts: { headers?: any } = {},
  apiRoot: string | null = null
) => {
  const headers = opts.headers || {};
  const apiRootPath = isEmptyString(apiRoot)
    ? `${app.params.apiDomain}/${app.params.apiVersion}`
    : apiRoot;

  if (method === 'GET') {
    apiPath =
      apiPath.indexOf('?') === -1
        ? `${apiPath}?__rnd=${getRandomCode(4)}`
        : `${apiPath}&__rnd=${getRandomCode(4)}`;
  }
  const endpoint = `${apiRootPath}/${apiPath}`;

  return {
    ...opts,
    url: endpoint,
    method,
    timeout: 30000,
    headers: {
      ...headers,
      'Accept': 'application/json',
      'Content-Type': headers['Content-Type'] || 'application/json'
    }
  };
};

/**
 * 与服务器交互数据
 * @param {*} commit
 * @param {*} config
 * @param {*} types
 */
const apiFetchAction = async (
  commit: Commit,
  apiPath: string,
  method: Method,
  types: ITypes,
  payload: any,
  userTest = false,
  apiRoot: string | null = null
) => {
  commit(types.REQUEST);
  // 是否使用测试数据
  if (userTest) {
    await new Promise((resolve, reject) => {
      setTimeout(() => {
        commit(types.SUCCESS, { req: payload.data, res: testData[apiPath] });
      }, 500);
    });
  } else {
    const opts = await authApiBefore(payload);
    const config: AxiosRequestConfig = getApiFetchOptions(
      apiPath,
      method,
      opts,
      apiRoot
    );
    if (method === 'GET' && payload) config.params = payload.data;
    if (method === 'POST' && payload) config.data = payload.data;
    if (method === 'DELETE' && payload) config.params = payload.data;
    await new Promise((resolve, reject) => {
      axios(config)
        .then((res: AxiosResponse) => {
          // 陪伴接口数据转换
          if (!!res.data && typeof res.data === 'string') {
            res.data = JSON.parse(res.data);
          }
          if (
            !res.data.hasOwnProperty('Success') &&
            !res.data.hasOwnProperty('Message')
          ) {
            res.data.Success = res.data.code === 200;
            res.data.Message = res.data.msg;
            res.data.Data = res.data.data;
          }
          if (res.status === 200 && res.data.Success) {
            commit(types.SUCCESS, {
              req: payload ? payload.data : {},
              res: res.data
            });
            resolve({ req: payload ? payload.data : {}, res: res.data });
          } else {
            commit(types.FAILED, {
              req: payload ? payload.data : {},
              res: res.data
            });
            reject({ req: payload ? payload.data : {}, res: res.data });
          }
        })
        .catch((err: any) => {
          commit(types.FAILED, { req: payload ? payload.data : {}, err });
          reject({ req: payload ? payload.data : {}, err });
        });
    });
  }
};

/**
 * 与服务器交互数据
 */
const apiFetch = async (
  apiPath: string,
  method: string,
  types: ITypes,
  payload: any,
  userTest = false
) => {
  let data: any = [];

  const opts = await authApiBefore(payload);
  const config: AxiosRequestConfig = getApiFetchOptions(apiPath, method, opts);
  if (method === 'GET' && payload) config.params = payload.data;
  if (method === 'POST' && payload) config.data = payload.data;
  await axios(config).then((res: AxiosResponse) => {
    if (res.status === 200 && res.data.code === 0) {
      data = res.data.data;
    }
  });

  return data;
};

/**
 * 与服务器交互数据(获取数据)
 */
const apiFetchData = async (
  commit: Commit,
  apiPath: string,
  method: string,
  types: ITypes,
  payload: any,
  userTest = false,
  apiRoot: string | null = null
) => {
  let data: any = [];
  // 是否使用测试数据
  if (userTest) {
    data = testData[apiPath];
  } else {
    const opts = await authApiBefore(payload);
    const config: AxiosRequestConfig = getApiFetchOptions(
      apiPath,
      method,
      opts,
      apiRoot
    );
    if (method === 'GET' && payload) config.params = payload.data;
    if (method === 'POST' && payload) config.data = payload.data;
    await axios(config)
      .then((res: AxiosResponse) => {
        if (res.status === 200 && res.data.Success) {
          data = res.data.Data;
          commit(types.SUCCESS, {
            req: payload ? payload.data : {},
            res: res.data
          });
        } else {
          commit(types.FAILED, {
            req: payload ? payload.data : {},
            res: res.data
          });
        }
      })
      .catch((err: any) => {
        commit(types.FAILED, { req: payload ? payload.data : {}, err });
      });
  }

  return data;
};

export { apiFetchAction, apiFetch, apiFetchData, createType };

// 如果api请求需要使用模拟数据
const testData: { [key: string]: any } = {};
